import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Plus, Pencil, Trash2, Download, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { EASE } from "../../components/motion";

const ENQUIRY_STATUSES = ["new", "in_review", "closed"];
const APPLICATION_STATUSES = ["new", "shortlisted", "rejected", "hired"];
const inputCls = "w-full px-3 py-2.5 rounded-lg bg-[#F1F5F9] border border-slate-300 text-sm text-slate-700 placeholder:text-slate-600 focus:outline-none focus:border-blue-600/60 transition-colors";

const statusBadge = (s) => {
  const map = {
    new: "bg-blue-50/70 text-blue-600 border-blue-200/50",
    in_review: "bg-amber-50 text-amber-700 border-amber-200",
    closed: "bg-slate-100 text-slate-600 border-slate-300",
    shortlisted: "bg-emerald-50 text-emerald-600 border-emerald-200",
    rejected: "bg-red-50 text-red-600 border-red-200",
    hired: "bg-emerald-50 text-emerald-600 border-emerald-200",
  };
  return `px-2 py-0.5 rounded-full text-[11px] font-mono border ${map[s] || map.new}`;
};

async function downloadFile(path, filename) {
  try {
    const res = await api.get(`/files/${path}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed");
  }
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{label}</span>
      <input {...props} className={inputCls} />
    </label>
  );
}

function EditorModal({ title, onClose, onSave, children, submitting }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}
      data-testid="editor-modal-overlay">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }}
        transition={{ duration: 0.3, ease: EASE }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-panel border border-slate-200 rounded-2xl p-7 max-h-[90vh] overflow-y-auto" data-testid="editor-modal">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} data-testid="editor-close" className="p-2 text-slate-500 hover:text-slate-700"><X size={18} /></button>
        </div>
        <form onSubmit={onSave} className="space-y-4">
          {children}
          <button type="submit" disabled={submitting} data-testid="editor-save-button"
            className="w-full py-3 rounded-full bg-blue-600 text-[#FFFFFF] font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50">
            {submitting ? "Saving…" : "Save"}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [applications, setApplications] = useState([]);
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [editor, setEditor] = useState(null); // {type: 'project'|'job', data}
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/admin/login", { replace: true });
  }, [user, navigate]);

  const load = useCallback(() => {
    if (!user) return;
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/admin/applications").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, [user]);

  useEffect(load, [load]);

  if (!user) {
    return <main className="min-h-screen flex items-center justify-center" data-testid="admin-loading"><span className="text-slate-500 text-sm font-mono">Loading…</span></main>;
  }

  const setStatus = async (kind, id, status) => {
    try {
      await api.patch(`/admin/${kind}/${id}`, { status });
      toast.success("Status updated");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveEditor = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const { type, data } = editor;
    try {
      if (type === "project") {
        const payload = { ...data, technologies: String(data.technologies || "").split(",").map((t) => t.trim()).filter(Boolean) };
        if (data.id) await api.put(`/admin/projects/${data.id}`, payload);
        else await api.post("/admin/projects", payload);
      } else {
        if (data.id) await api.put(`/admin/jobs/${data.id}`, data);
        else await api.post("/admin/jobs", data);
      }
      toast.success("Saved");
      setEditor(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${type}/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const TABS = [
    ["overview", "Overview"], ["enquiries", "Enquiries"], ["applications", "Applications"],
    ["projects", "Projects"], ["jobs", "Jobs"],
  ];

  const statCards = stats ? [
    ["Total Enquiries", stats.enquiries_total], ["New Enquiries", stats.enquiries_new],
    ["Applications", stats.applications_total], ["New Applications", stats.applications_new],
    ["Projects", stats.projects], ["Active Jobs", stats.jobs],
  ] : [];

  return (
    <main className="min-h-screen" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FFFFFF]/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-md bg-blue-600/10 border border-blue-600/40 flex items-center justify-center font-heading font-extrabold text-blue-600 text-xs">SGS</span>
            <span className="font-heading font-bold text-slate-900 text-sm">Admin Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-slate-500" data-testid="admin-user-email">{user.email}</span>
            <button onClick={() => navigate("/")} data-testid="admin-view-site" className="p-2 text-slate-500 hover:text-blue-600 transition-colors" title="View site">
              <ExternalLink size={16} />
            </button>
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-button"
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-slate-300 text-xs text-slate-700 hover:border-red-300 hover:text-red-600 transition-colors">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex gap-1 overflow-x-auto pb-2">
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${tab === id ? "bg-blue-50/50 text-blue-600 border border-blue-200/50" : "text-slate-500 hover:text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {tab === "overview" && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-overview">
            {statCards.map(([label, value], i) => (
              <div key={label} className="bg-panel border border-slate-200 rounded-xl p-6" data-testid={`admin-stat-${i}`}>
                <div className="font-heading text-3xl font-bold text-blue-600">{value}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1.5">{label}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-3" data-testid="admin-dashboard-leads-table">
            {enquiries.length === 0 && <p className="text-sm text-slate-500">No enquiries yet.</p>}
            {enquiries.map((enq, i) => (
              <div key={enq.id} className="bg-panel border border-slate-200 rounded-xl p-5" data-testid={`enquiry-row-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-semibold text-slate-900">{enq.name}</h3>
                      <span className={statusBadge(enq.status)}>{enq.status}</span>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-blue-600/70">{enq.requirement_type}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {enq.company && `${enq.company} · `}{enq.email}{enq.phone && ` · ${enq.phone}`}{enq.country && ` · ${enq.country}`}
                    </p>
                    <p className="mt-2.5 text-sm text-slate-600 leading-relaxed max-w-3xl">{enq.description}</p>
                    <p className="mt-2 font-mono text-[10px] text-slate-600">{new Date(enq.created_at).toLocaleString()}{enq.timeline && ` · Timeline: ${enq.timeline}`}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {enq.attachment_path && (
                      <button onClick={() => downloadFile(enq.attachment_path, enq.attachment_name)} data-testid={`enquiry-attachment-${i}`}
                        className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:text-blue-600 hover:border-blue-600/50 transition-colors" title="Download attachment">
                        <Download size={15} />
                      </button>
                    )}
                    <select value={enq.status} onChange={(e) => setStatus("enquiries", enq.id, e.target.value)}
                      data-testid={`enquiry-status-${i}`} className="px-3 py-2 rounded-lg bg-[#F1F5F9] border border-slate-300 text-xs text-slate-700">
                      {ENQUIRY_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "applications" && (
          <div className="space-y-3" data-testid="admin-applications-table">
            {applications.length === 0 && <p className="text-sm text-slate-500">No applications yet.</p>}
            {applications.map((app, i) => (
              <div key={app.id} className="bg-panel border border-slate-200 rounded-xl p-5" data-testid={`application-row-${i}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading font-semibold text-slate-900">{app.name}</h3>
                      <span className={statusBadge(app.status)}>{app.status}</span>
                      <span className="font-mono text-[10px] uppercase tracking-wider text-blue-600/70">{app.position}</span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">{app.email} · {app.phone}{app.experience && ` · ${app.experience}`}</p>
                    {app.message && <p className="mt-2.5 text-sm text-slate-600 leading-relaxed max-w-3xl">{app.message}</p>}
                    <p className="mt-2 font-mono text-[10px] text-slate-600">{new Date(app.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => downloadFile(app.resume_path, app.resume_name)} data-testid={`application-resume-${i}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-xs text-slate-700 hover:text-blue-600 hover:border-blue-600/50 transition-colors">
                      <Download size={13} /> Resume
                    </button>
                    <select value={app.status} onChange={(e) => setStatus("applications", app.id, e.target.value)}
                      data-testid={`application-status-${i}`} className="px-3 py-2 rounded-lg bg-[#F1F5F9] border border-slate-300 text-xs text-slate-700">
                      {APPLICATION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "projects" && (
          <div data-testid="admin-projects-table">
            <button onClick={() => setEditor({ type: "project", data: { name: "", client_sector: "", location: "", objective: "", scope: "", challenges: "", solution: "", outcome: "", technologies: "", image: "", featured: false } })}
              data-testid="admin-new-project" className="mb-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-[#FFFFFF] text-sm font-semibold hover:bg-blue-700 transition-colors">
              <Plus size={15} /> New Project
            </button>
            <div className="space-y-3">
              {projects.map((p, i) => (
                <div key={p.id} className="flex items-center justify-between gap-4 bg-panel border border-slate-200 rounded-xl p-5" data-testid={`project-row-${i}`}>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900">{p.name}</h3>
                    <p className="mt-0.5 text-xs text-slate-500">{p.client_sector} · {p.location}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setEditor({ type: "project", data: { ...p, technologies: (p.technologies || []).join(", ") } })}
                      data-testid={`project-edit-${i}`} className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:text-blue-600 transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => remove("projects", p.id)} data-testid={`project-delete-${i}`}
                      className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "jobs" && (
          <div data-testid="admin-jobs-table">
            <button onClick={() => setEditor({ type: "job", data: { title: "", department: "", location: "Patna, India", type: "Full-time", experience: "", description: "", active: true } })}
              data-testid="admin-new-job" className="mb-4 flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-[#FFFFFF] text-sm font-semibold hover:bg-blue-700 transition-colors">
              <Plus size={15} /> New Job
            </button>
            <div className="space-y-3">
              {jobs.map((j, i) => (
                <div key={j.id} className="flex items-center justify-between gap-4 bg-panel border border-slate-200 rounded-xl p-5" data-testid={`job-row-${i}`}>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900">{j.title}</h3>
                    <p className="mt-0.5 text-xs text-slate-500">{j.department} · {j.location} · {j.type}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setEditor({ type: "job", data: j })} data-testid={`job-edit-${i}`}
                      className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:text-blue-600 transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => remove("jobs", j.id)} data-testid={`job-delete-${i}`}
                      className="p-2 rounded-lg border border-slate-300 text-slate-600 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {editor && (
          <EditorModal title={editor.data.id ? "Edit" : "Create"} submitting={submitting}
            onClose={() => setEditor(null)} onSave={saveEditor}>
            {editor.type === "project" ? (
              <>
                <Field label="Project name *" required value={editor.data.name} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, name: e.target.value } })} data-testid="editor-project-name" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Client / Sector" value={editor.data.client_sector} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, client_sector: e.target.value } })} />
                  <Field label="Location" value={editor.data.location} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, location: e.target.value } })} />
                </div>
                {["objective", "scope", "challenges", "solution", "outcome"].map((k) => (
                  <label key={k} className="block">
                    <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{k}</span>
                    <textarea rows={2} value={editor.data[k] || ""} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, [k]: e.target.value } })} className={inputCls} />
                  </label>
                ))}
                <Field label="Technologies (comma separated)" value={editor.data.technologies} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, technologies: e.target.value } })} />
                <Field label="Image URL" value={editor.data.image} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, image: e.target.value } })} />
                <label className="flex items-center gap-2.5">
                  <input type="checkbox" checked={!!editor.data.featured} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, featured: e.target.checked } })} className="w-4 h-4 accent-blue-600" />
                  <span className="text-sm text-slate-600">Featured project</span>
                </label>
              </>
            ) : (
              <>
                <Field label="Job title *" required value={editor.data.title} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, title: e.target.value } })} data-testid="editor-job-title" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Department" value={editor.data.department} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, department: e.target.value } })} />
                  <Field label="Location" value={editor.data.location} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, location: e.target.value } })} />
                  <Field label="Type" value={editor.data.type} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, type: e.target.value } })} />
                  <Field label="Experience" value={editor.data.experience} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, experience: e.target.value } })} />
                </div>
                <label className="block">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">Description</span>
                  <textarea rows={3} value={editor.data.description || ""} onChange={(e) => setEditor({ ...editor, data: { ...editor.data, description: e.target.value } })} className={inputCls} />
                </label>
              </>
            )}
          </EditorModal>
        )}
      </AnimatePresence>
    </main>
  );
}
